import type { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"

export interface PillTab<T extends string> {
  key: T
  label: string
  icon?: LucideIcon
}

/**
 * Tab pills no estilo "glass" do host Koortex (feedback 2026-07-04 #1):
 * contentor rounded-full translúcido com blur, pill ativa preenchida.
 */
export function PillTabs<T extends string>({
  tabs,
  value,
  onChange,
  size = "md",
  className,
}: {
  tabs: Array<PillTab<T>>
  value: T
  onChange: (key: T) => void
  size?: "sm" | "md"
  className?: string
}) {
  return (
    <div
      className={cn(
        "inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-border/60 bg-card/70 p-1 shadow-sm backdrop-blur",
        className,
      )}
    >
      {tabs.map((t) => {
        const active = t.key === value
        const Icon = t.icon
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-full font-medium transition-colors",
              size === "sm" ? "px-3 py-1 text-xs" : "px-3.5 py-1.5 text-sm",
              active
                ? "bg-accent text-accent-foreground shadow-sm"
                : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground",
            )}
          >
            {Icon ? <Icon className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} /> : null}
            {t.label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Compact tab bar for narrow (mobile) viewports — the same "glass" pill, but
 * the active tab expands to icon + label (filled with the tenant accent) while
 * inactive tabs are icon-only, so all 6–7 destinations fit one row without
 * horizontal scrolling on a phone. Rendered inline at the top; hidden on ≥ sm
 * (where the full PillTabs takes over). Has its own `sm:hidden`.
 */
export function MobileTabBar<T extends string>({
  tabs,
  value,
  onChange,
  className,
}: {
  tabs: Array<PillTab<T>>
  value: T
  onChange: (key: T) => void
  className?: string
}) {
  return (
    <div
      className={cn(
        "flex max-w-full items-center gap-0.5 overflow-x-auto rounded-full border border-border/60 bg-card/70 p-1 shadow-sm backdrop-blur sm:hidden",
        className,
      )}
    >
      {tabs.map((t) => {
        const active = t.key === value
        const Icon = t.icon
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            aria-label={t.label}
            title={t.label}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-full font-medium transition-colors",
              active
                ? "bg-accent px-3.5 py-2 text-sm text-accent-foreground shadow-sm"
                : "px-2.5 py-2 text-muted-foreground hover:bg-secondary/70 hover:text-foreground",
            )}
          >
            {Icon ? <Icon className="h-[18px] w-[18px] shrink-0" /> : null}
            {active ? <span className="max-w-[9rem] truncate">{t.label}</span> : null}
          </button>
        )
      })}
    </div>
  )
}
